import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {Card} from "primereact/card";
import {Button} from "primereact/button";
import {Toast} from "primereact/toast";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import moment from "moment";

import useApi from "../../hooks/useApi";
import useDelete from "../../hooks/useDelete";
import {webInfo} from "../../utils/webInfo";
import callToast, {catchError} from "../../utils/helper";
import TableSkeleton from "../../Loader/Loader";
import ErrorPresentor from "../../ErrorPresentor/ErrorPresentor";
import usePost from "../../hooks/usePost";

interface Department {
    id?: number;
    name: string;
    description: string;
    created_at?: string;
    updated_at?: string;
}

interface Company {
    id: number;
    name: string;
    description: string;
    slug?: string;
    departments?: Department[];
    created_at: string;
    updated_at: string;
}

interface CompanyForm {
    name: string;
    description: string;
    departments: Department[];
}

const emptyDepartment: Department = {
    name: "",
    description: "",
};

const CompanyDetails = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const toast = useRef<Toast | null>(null);
    const {get, loading, error} = useApi<any>();
    const {post, loading: updateLoading} = usePost();
    const {remove, loading: deleteLoading} = useDelete();

    const [company, setCompany] = useState<Company | null>(null);
    const [companyForm, setCompanyForm] = useState<CompanyForm>({
        name: "",
        description: "",
        departments: [],
    });
    const [visibleUpdate, setVisibleUpdate] = useState(false);
    const [visibleConfirmation, setVisibleConfirmation] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const detailsUrl = `${webInfo.baseUrl}/api/admin/companies/${id}`;

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const res: any = await get(detailsUrl);
                const responseData = res?.data ?? res;
                setCompany(responseData);
                setCompanyForm({
                    name: responseData?.name ?? "",
                    description: responseData?.description ?? "",
                    departments: responseData?.departments?.length
                        ? responseData.departments.map((department: Department) => ({
                            id: department.id,
                            name: department.name ?? "",
                            description: department.description ?? "",
                            created_at: department.created_at,
                            updated_at: department.updated_at,
                        }))
                        : [{...emptyDepartment}],
                });
            } catch (err) {
                console.error("Failed to fetch company", err);
            }
        };
        if (id) {
            fetchCompany();
        }
        // eslint-disable-next-line
    }, [id, refresh]);

    const handleCompanyChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCompanyForm({
            ...companyForm,
            [event.target.name]: event.target.value,
        });
    };

    const handleDepartmentChange = (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const departments = companyForm.departments.map((department, departmentIndex) => {
            if (departmentIndex !== index) return department;
            return {
                ...department,
                [event.target.name]: event.target.value,
            };
        });
        setCompanyForm({
            ...companyForm,
            departments,
        });
    };

    const addDepartment = () => {
        setCompanyForm({
            ...companyForm,
            departments: [...companyForm.departments, {...emptyDepartment}],
        });
    };

    const removeDepartment = (index: number) => {
        setCompanyForm({
            ...companyForm,
            departments: companyForm.departments.filter((_, departmentIndex) => departmentIndex !== index),
        });
    };

    const openUpdateDialog = () => {
        if (!company) return;

        setCompanyForm({
            name: company.name ?? "",
            description: company.description ?? "",
            departments: company.departments?.length
                ? company.departments.map((department) => ({
                    id: department.id,
                    name: department.name ?? "",
                    description: department.description ?? "",
                    created_at: department.created_at,
                    updated_at: department.updated_at,
                }))
                : [{...emptyDepartment}],
        });

        setVisibleUpdate(true);
    };

    const closeUpdateDialog = () => {
        setVisibleUpdate(false);
    };

    const closeDeleteDialog = () => {
        setVisibleConfirmation(false);
    };

    const updateCompany = async () => {
        if (!company) return;

        if (!companyForm.name.trim()) {
            callToast(toast, "warn", "Company name is required");
            return;
        }

        const updateUrl = `${webInfo.baseUrl}/api/admin/companies/${company.id}`;

        const payload = {
            name: companyForm.name,
            description: companyForm.description,
            departments: companyForm.departments.filter((department) => (
                department.name.trim() || department.description.trim()
            )),
        };

        try {
            const res: any = await post(updateUrl,true, payload);
            if (res?.success === false) {
                callToast(toast, false, res?.error?.message ?? "Failed to update company");
                return;
            }
            callToast(toast, true, "Company updated successfully");
            setVisibleUpdate(false);
            setRefresh(!refresh);
        } catch (e) {
            catchError(e);
            callToast(toast, false, "Failed to update company");
        }
    };

    const deleteCompany = async () => {
        if (!company) return;
        const deleteUrl = `${webInfo.baseUrl}/api/admin/companies/${company.id}`;
        try {
            const res: any = await remove(deleteUrl);
            if (res?.success === false) {
                callToast(toast, false, res?.error?.message ?? "Failed to delete company");
                return;
            }

            callToast(toast, true, "Company deleted successfully");
            navigate('/companies');
        } catch (e) {
            catchError(e);
            callToast(toast, false, "Failed to delete company");
        }
    };

    const createdAtTemplate = (rowData: Department) => (
        <span>{rowData.created_at ? moment(rowData.created_at).format("LLL") : "-"}</span>
    );

    const updatedAtTemplate = (rowData: Department) => (
        <span>{rowData.updated_at ? moment(rowData.updated_at).format("LLL") : "-"}</span>
    );

    const descriptionTemplate = (rowData: Department) => (
        <span>{rowData.description || "-"}</span>
    );

    const updateFooterContent = (
        <div className="flex justify-content-end align-items-center">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={closeUpdateDialog}
            />

            <Button
                label="Update"
                icon="pi pi-check"
                className="ml-2"
                loading={updateLoading}
                disabled={!companyForm.name.trim()}
                onClick={updateCompany}
            />
        </div>
    );

    const deleteFooterContent = (
        <div className="flex justify-content-end align-items-center">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={closeDeleteDialog}
            />

            <Button
                label="Delete"
                severity="danger"
                className="ml-2"
                icon="pi pi-trash"
                loading={deleteLoading}
                onClick={deleteCompany}
            />
        </div>
    );

    const header = (
        <div className="flex align-items-center justify-content-between p-4 pb-0">
            <h4 className="m-0">Company Details</h4>
            <div className="flex gap-2">
                <Button
                    label="Update"
                    icon="pi pi-pencil"
                    onClick={openUpdateDialog}
                    disabled={!company}
                />

                <Button
                    label="Delete"
                    icon="pi pi-trash"
                    severity="danger"
                    onClick={() => setVisibleConfirmation(true)}
                    disabled={!company}
                />
            </div>
        </div>
    );

    if (loading) {
        return (
            <Card header={header}>
                <TableSkeleton/>
            </Card>
        );
    }

    if (error) {
        return (
            <Card header={header}>
                <ErrorPresentor error={error}/>
            </Card>
        );
    }

    return (
        <Card header={header} style={{maxHeight: '88vh', minHeight: '88vh', overflowY: 'auto'}}>
            <Toast ref={toast}/>
            <Dialog
                visible={visibleUpdate}
                modal
                header="Update Company"
                footer={updateFooterContent}
                style={{width: '90rem', maxWidth: '95vw'}}
                onHide={closeUpdateDialog}
            >
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <div className="my-2">
                            <label htmlFor="name" className="font-medium block">
                                Company Name *
                            </label>

                            <InputText
                                id="name"
                                name="name"
                                value={companyForm.name}
                                onChange={handleCompanyChange}
                                className="w-full mt-2"
                                placeholder="Company name"
                            />
                        </div>

                        <div className="my-3">
                            <label htmlFor="description" className="font-medium">
                                Description
                            </label>

                            <InputTextarea
                                id="description"
                                name="description"
                                value={companyForm.description}
                                onChange={handleCompanyChange}
                                className="w-full mt-2"
                                rows={8}
                                autoResize
                                placeholder="Description"
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="flex align-items-center justify-content-between mt-2 mb-3">
                            <h5 className="m-0">Departments</h5>

                            <Button
                                type="button"
                                label="Add New"
                                icon="pi pi-plus"
                                className="p-button-sm"
                                onClick={addDepartment}
                            />
                        </div>

                        {companyForm.departments.map((department, index) => (
                            <Card key={department.id ?? index} className="mb-3">
                                <div className="grid">
                                    <div className="col-12">
                                        <label className="font-medium block">
                                            Department Name
                                        </label>

                                        <InputText
                                            name="name"
                                            value={department.name}
                                            onChange={(event) => handleDepartmentChange(index, event)}
                                            className="w-full mt-2"
                                            placeholder="Department name"
                                        />
                                    </div>

                                    <div className="col-12">
                                        <label className="font-medium">
                                            Department Description
                                        </label>

                                        <InputTextarea
                                            name="description"
                                            value={department.description}
                                            onChange={(event) => handleDepartmentChange(index, event)}
                                            className="w-full mt-2"
                                            rows={2}
                                            autoResize
                                            placeholder="Department description"
                                        />
                                    </div>
                                </div>

                                <Button
                                    size="small"
                                    type="button"
                                    label="Remove"
                                    icon="pi pi-trash"
                                    severity="danger"
                                    outlined
                                    disabled={companyForm.departments.length === 1}
                                    onClick={() => removeDepartment(index)}
                                />
                            </Card>
                        ))}
                    </div>
                </div>
            </Dialog>

            <Dialog
                visible={visibleConfirmation}
                modal
                header="Remove company confirmation"
                footer={deleteFooterContent}
                style={{width: '30rem'}}
                onHide={closeDeleteDialog}
            >
                <p className="m-0">
                    Are you sure you want to delete this company? Once deleted, it cannot be viewed again.
                </p>
            </Dialog>

            {company ? (
                <div>
                    <div className="grid mb-4">
                        <div className="col-12 md:col-6">
                            <p><strong>Name:</strong> {company.name || "-"}</p>
                            <p><strong>Description:</strong> {company.description || "-"}</p>
                        </div>
                    </div>

                    <div className="flex align-items-center justify-content-between mb-3">
                        <h5 className="m-0">Departments</h5>

                        {/*<Button*/}
                        {/*    label="Update Departments"*/}
                        {/*    icon="pi pi-pencil"*/}
                        {/*    className="p-button-sm"*/}
                        {/*    onClick={openUpdateDialog}*/}
                        {/*/>*/}
                    </div>

                    <DataTable
                        value={company.departments ?? []}
                        stripedRows
                        emptyMessage="No departments found"
                    >
                        <Column
                            field="created_at"
                            header="Created At"
                            body={createdAtTemplate}
                            sortable
                        />

                        <Column
                            field="name"
                            header="Department Name"
                            sortable
                        />

                        <Column
                            field="description"
                            header="Description"
                            body={descriptionTemplate}
                        />

                        <Column
                            field="updated_at"
                            header="Updated At"
                            body={updatedAtTemplate}
                            sortable
                        />
                    </DataTable>
                </div>
            ) : (
                <p>No company found.</p>
            )}
        </Card>
    );
};

export default CompanyDetails;